import React, { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Collapse, Stack, Typography } from "@mui/material";
import "./index.css";
import { getCurrentAuth } from "../../hooks/useAxiosPrivate";
import PageRoutes, { APIRoutes, INSIDER_PLAN_ID } from "../../utils/constants";
import {
  trackGaEvent,
  planTierFromId,
  stashPendingGameId,
} from "../../utils/gaUser";
import { useNavigate, useSearchParams } from "react-router-dom";
import useConfig from "../../hooks/useConfig";
import { toast } from "react-toastify";
import DialogBox from "../../Common/Component/DialogBox";
import HttpService from "../../hooks/Https-services";
import { useStripe } from "@stripe/react-stripe-js";
import CustomToggle from "./CustomToggle";
import UpgradeModel from "./UpgradeModel";
import { useSubscriptionStatus } from "../../Common/Contex/SubscriptionStatusContex";
import { buildPayUrl } from "../../utils/paymentRecovery";

const Passes = ({ isHomePage, handleRefresh }: any) => {
  const auth = getCurrentAuth();
  const [openColleps, setopenColleps] = useState(true);
  const navigate = useNavigate();
  const [isSkeleton, setIsSkeleton] = useState(false);
  const [currentPlan, setCurrentPlan] = useState({} as any);
  const [isSubscribe, setIsSubscribe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const configData = useConfig();
  const [alignment, setAlignment] = React.useState<boolean | null>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const gameId = searchParams.get("id");
  const { pendingPayment } = useSubscriptionStatus();

  // HTPTV-695: when this exact plan has an outstanding invoice, its card routes
  // to the in-app recovery checkout (settling the existing PaymentIntent) instead
  // of BUY, which would create a duplicate subscription. buildPayUrl returns null
  // unless the pending payment is real (amountDue > 0, priceId + paymentRowId
  // present), so non-matching plans fall through to their normal button.
  const completePaymentUrlFor = (el: any) =>
    pendingPayment &&
    String(pendingPayment?.priceId) === String(el?.PlanID)
      ? buildPayUrl(pendingPayment)
      : null;

  // HTPTV-695: while a real pending payment exists (on exactly one plan), the
  // other plan cards must show no action at all - the user has to finish the
  // open invoice first, and buying a different plan would create a duplicate
  // subscription. Only the matching card shows Complete Payment (above).
  const hasPendingPayment = Boolean(buildPayUrl(pendingPayment));

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // HTPTV-685: plans/pricing page viewed (dedicated page, not the homepage embed).
  useEffect(() => {
    if (!isHomePage)
      trackGaEvent("view_item_list", { item_list_name: "plans_page" });
  }, []);

  const GetUserSubscriptionData = async () => {
    try {
      const res = await HttpService.get(APIRoutes.GetSubscriptionDetials);

      if (res?.data?.data?.plan_type_id == 3) {
        setAlignment(false);
      } else {
        setAlignment(res?.data?.data.name === "Monthly" ? false : true);
      }
      setCurrentPlan(res?.data?.data);
    } catch (error) {}
  };

  const CheckUserSubscribe = async () => {
    setIsSkeleton(true);

    try {
      const res = await HttpService.get(APIRoutes.CheckSubscribe);
      if (res?.data?.data?.isSubscribe) {
        setIsSubscribe(true);
        await GetUserSubscriptionData();
        handleRefresh();
        setIsSkeleton(false);
      } else {
        setIsSubscribe(false);
        setIsSkeleton(false);
      }
    } catch (error) {
      setIsSkeleton(false);
    }
  };

  const handleSubmit = async (el: any) => {
    setIsSkeleton(true);
    // HTPTV-685 (phase 2): remember the game so a single-game purchase can carry game_id.
    if (gameId) stashPendingGameId(gameId);
    try {
      const response: any = await HttpService.post(APIRoutes.SubscribePlan, {
        priceId: el?.PlanID,
        planTypeId: el?.PlanTypeID,
        gameId: gameId,
      });
      if (response) {
        const encodedStripeLink = encodeURIComponent(
          response.data.data.clientSecret
        );
        const encodedPaymentId = encodeURIComponent(response.data.data.plan_id);
        const encodedSubscriptionId = encodeURIComponent(
          response.data.data.paymentId
        );
        navigate(
          PageRoutes.paymentPage
            .replace(":clientSecret", encodedStripeLink)
            .replace(":plan_id", encodedPaymentId)
            .replace(":paymentId", encodedSubscriptionId)
        );
      }
      setIsSkeleton(false);
    } catch (error: any) {
      toast.error(error.response.data.message);
      setIsSkeleton(false);
    }
  };
  const handleMangePlan = async () => {
    if (!auth.AccessToken) {
      navigate(PageRoutes.Login);
    }
    setIsLoading(true);
    const cancel_at_period_end =
      currentPlan?.data &&
      currentPlan?.data[0].isRecurring &&
      !currentPlan?.data[0].cancel_date
        ? true
        : false;
    // HTPTV-685: this button also reactivates ("Renew plan") - only instrument cancels.
    if (cancel_at_period_end) {
      const planId = currentPlan?.data?.[0]?.plan_id;
      trackGaEvent(
        "cancel_flow_start",
        planId ? { plan_id: String(planId) } : {}
      );
    }
    try {
      const response: any = await HttpService.post(
        APIRoutes.ChangePlanDetails,
        {
          subscriptionId: currentPlan?.subscriptionId,
          cancel_at_period_end: cancel_at_period_end,
        }
      );

      if (cancel_at_period_end) {
        // HTPTV-685: cancellation completed in our UI.
        const cancelTier = planTierFromId(
          currentPlan?.plan_type_id ?? currentPlan?.PlanTypeID
        );
        trackGaEvent(
          "subscription_cancelled",
          cancelTier ? { plan_tier: cancelTier } : {}
        );
      }
      setIsLoading(false);
      CheckUserSubscribe();
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
      setIsLoading(false);
    }
  };
  const stripe: any = useStripe();
  const handleUpgrade = async (el: any, coupenCode: string) => {
    if (!auth.AccessToken) {
      navigate(PageRoutes.Login);
    }

    setIsLoading(true);

    try {
      const response: any = await HttpService.post(APIRoutes.CustomerPortal, {
        subscriptionId: currentPlan?.subscriptionId,
        priceId: el?.PlanID,
        customerId: currentPlan?.costomer_id,
        planTypeId: el?.PlanTypeID,
        coupenCode: coupenCode,
      });
      if (response?.data?.data?.clientSecret) {
        const data = await stripe.handleNextAction({
          clientSecret: response?.data?.data?.clientSecret,
        });
        if (data?.error) {
          toast.error(data?.error?.message);
          setIsLoading(false);
        }
      } else if (
        response?.data?.data?.latest_invoice?.payment_intent?.last_payment_error
          ?.message
      ) {
        toast.error(
          response?.data?.data?.latest_invoice?.payment_intent
            ?.last_payment_error?.message
        );
        setIsLoading(false);
      }
      setIsLoading(false);
      CheckUserSubscribe();
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
      setIsLoading(false);
    }
  };
  const handelRestartPause = async () => {
    try {
      setIsLoading(true);

      const res = await HttpService.post(
        `${APIRoutes.PauseSubscription.replace(
          ":id",
          currentPlan?.subscription.id
        )}?isPause=false`,
        {
          date: "",
        }
      );
      await GetUserSubscriptionData();
      handleRefresh();
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      // handleChange(false);
      toast.error(error.response.data.message);
    }
  };

  const GetPlans = async () => {
    try {
      setIsSkeleton(true);
      const res = await HttpService.get(APIRoutes.GetPlans);

      // Normalise to an array. The render does plans?.filter(...), which optional
      // chaining protects from null/undefined but not from a non-array payload,
      // so any unexpected response shape crashed the whole Passes page.
      setPlans(Array.isArray(res?.data?.data) ? res.data.data : []);

      setIsSkeleton(false);
    } catch (error) {
      setIsSkeleton(false);
    }
  };
  useEffect(() => {
    if (auth.AccessToken) {
      CheckUserSubscribe();
    }
    GetPlans();
  }, []);
  const findInsiderPlan = (plan: any) => {
    return (
      plan?.PlanTypeID == INSIDER_PLAN_ID &&
      process.env.REACT_APP_INSIDER === "true"
    );
  };
  return (
    <Box>
      <div className="container-fluid">
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              bottom: "50%",
              top: "50%",
              right: 0,
              zIndex: 1301,
              left: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",

                  gap: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, fontStyle: "normal" }}
                >
                  Your order is being processed.
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, fontStyle: "normal" }}
                >
                  Please do not refresh the page
                </Typography>

                <CircularProgress
                  size={48}
                  sx={{
                    color: "#bf311a",
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
        <Box
          sx={
            isLoading
              ? {
                  "-webkit-filter": "blur(5px)",
                  filter: " blur(5px)",
                }
              : {}
          }
        >
          {!isHomePage && (
            <Collapse in={openColleps}>
              <div
                style={{
                  maxWidth: "1110px",
                  margin: "0px auto",
                  padding: "10px",
                }}
              >
                <div>
                  <div
                    role="alert"
                    className="alert alert-secondary alert-dismissible fade show"
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: configData.planHeader,
                      }}
                    ></div>
                    <button
                      onClick={() => setopenColleps(false)}
                      type="button"
                      data-dismiss="alert"
                      aria-label="Close"
                      className="close"
                    >
                      <span>×</span>
                    </button>
                  </div>
                </div>
              </div>
            </Collapse>
          )}
          <div
            className="container-fluid"
            style={{
              maxWidth: isHomePage ? "100%" : "1110px",
              margin: "0px auto",
            }}
          >
            {currentPlan?.subscription?.pause_collection && (
              <Alert severity="warning">
                You currently have a subscription on pause. Click on your plan
                below to resume your subscription.
              </Alert>
            )}
            <Stack alignItems={"center"} sx={{ pb: 3 }}>
              <CustomToggle checked={alignment} setChecked={setAlignment} />
            </Stack>
            {isSkeleton ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                <CircularProgress
                  size={48}
                  sx={{
                    color: "#bf311a",
                  }}
                />
              </Box>
            ) : (
              <div
                style={{ marginTop: "1rem" }}
                className="main-package-wrapper main-container"
              >
                {plans
                  ?.filter((el: any) => {
                    if (el?.PlanTypeID != 3) {
                      if (
                        alignment
                          ? el?.Name === "Yearly"
                          : el?.Name === "Monthly"
                      ) {
                        return el;
                      }
                    } else {
                      return el;
                    }
                  })
                  ?.map((el: any) => (
                    <div
                      style={{
                        background: findInsiderPlan(el)
                          ? `linear-gradient(90deg, ${configData.primary} 4%,  ${configData.secondary} 0)
      top / 100% 34% no-repeat,
    linear-gradient(90deg,  ${configData.secondary} 50%,   ${configData.primary} 0) top / 100%
      82% no-repeat,
    linear-gradient(90deg,    ${configData.primary} 10%,  ${configData.secondary} 0) top / 100%
      100%`
                          : "",
                      }}
                      className={`pricing-box d-flex ${
                        findInsiderPlan(el) ? "box-price-insider" : ""
                      }`}
                    >
                      {findInsiderPlan(el) && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: -32,
                            m: "auto",
                            right: 0,
                            zIndex: 10,
                            left: 0,
                            width: "100%",
                            height: "45px",
                            overflow: "visible",
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              width: "100%",
                              height: "100%",
                            }}
                          >
                            {/* Main badge */}
                            <Box
                              sx={{
                                position: "absolute",
                                top: "8px",
                                left: "50%",
                                borderRadius: "5px",
                                transform: "translateX(-50%)",
                                width: "150px",
                                height: "50px",
                                backgroundColor: configData.secondary,
                                color: "white",
                                fontWeight: "bold",
                                fontSize: "18px",
                                letterSpacing: "0.5px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                clipPath:
                                  "polygon(0 0, 100% 0, 100% 65%, 50% 100%, 0 65%)",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexDirection: "column",
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    lineHeight: "16px",
                                    color: configData.tertiary,
                                    fontWeight: "bold",
                                    fontStyle: "normal",
                                  }}
                                >
                                  MOST
                                </Typography>
                                <Typography
                                  sx={{
                                    color: configData.tertiary,
                                    fontWeight: "bold",
                                    fontStyle: "normal",
                                  }}
                                  variant="body2"
                                >
                                  POPULAR
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )}

                      <div className="pricing-title">
                        <h3>{el?.PlanType}</h3>
                      </div>
                      <div
                        className="middel-part"
                        style={{ backgroundColor: "#f5f5f5" }}
                      >
                        <div className="pricing-feature">
                          <div
                            dangerouslySetInnerHTML={{
                              __html: el?.PlanContent,
                            }}
                            className="price-desc"
                          ></div>
                        </div>
                        {["nahl", "dev"].includes(configData?.name) &&
                          findInsiderPlan(el) && (
                            <div className="pricing-feature pricing-discount-container">
                              <Box
                                sx={{
                                  position: "relative",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  width: "100%",
                                  my: 1,
                                  pb: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    position: "relative",
                                    display: "inline-block",
                                    margin: "10px auto",
                                    padding: "5px 15px",
                                    backgroundColor: "rgba(0,0,0,0.05)",
                                    borderRadius: "4px",
                                    transform: "rotate(-2deg)",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: "bold",
                                      fontSize: "1.5rem",
                                      position: "relative",
                                      color: "#666",
                                      "&::after": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: "50%",
                                        width: "100%",
                                        height: "2px",
                                        backgroundColor:
                                          configData.secondary || "#bf311a",
                                        transform: "rotate(-5deg)",
                                      },
                                    }}
                                    variant="subtitle1"
                                  >
                                    {el?.SecondPrice}
                                  </Typography>
                                </Box>
                              </Box>
                            </div>
                          )}
                        <div
                          style={{
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          <div className="pricing-price">{el?.Price}</div>
                          {completePaymentUrlFor(el) ? (
                            <div
                              className="pricing-action"
                              onClick={() => {
                                const url = completePaymentUrlFor(el);
                                if (!isSkeleton && url) navigate(url);
                              }}
                            >
                              <button
                                disabled={isSkeleton}
                                style={{ cursor: "pointer" }}
                                className={configData.buttonCommonClass}
                              >
                                Complete Payment
                              </button>
                            </div>
                          ) : hasPendingPayment ? (
                            // Keep the button's vertical space (hidden) so the
                            // prices stay aligned across cards while only the
                            // plan with the open invoice offers an action.
                            <div className="pricing-action" aria-hidden="true">
                              <button
                                disabled
                                tabIndex={-1}
                                style={{
                                  visibility: "hidden",
                                  cursor: "default",
                                }}
                                className={configData.buttonCommonClass}
                              >
                                &nbsp;
                              </button>
                            </div>
                          ) : currentPlan?.isGift &&
                            el?.PlanTypeID != 3 ? (
                            <div
                              className="pricing-action"
                              onClick={() => {
                                // HTPTV-636: a gift recipient adds a card at their
                                // GIFTED tier only. Higher tiers are an upgrade
                                // taken AFTER conversion (so the prorated
                                // difference is charged then), not a free direct
                                // convert-to-higher-tier.
                                if (
                                  String(el?.PlanTypeID) ===
                                    String(
                                      currentPlan?.plan_type_id ??
                                        currentPlan?.PlanTypeID
                                    ) &&
                                  !isSkeleton &&
                                  el?.Status
                                )
                                  handleSubmit(el);
                              }}
                            >
                              <button
                                disabled={
                                  isSkeleton ||
                                  !el?.Status ||
                                  String(el?.PlanTypeID) !==
                                    String(
                                      currentPlan?.plan_type_id ??
                                        currentPlan?.PlanTypeID
                                    )
                                }
                                style={{ cursor: "pointer" }}
                                className={configData.buttonCommonClass}
                              >
                                {String(el?.PlanTypeID) ===
                                String(currentPlan?.plan_type_id ?? currentPlan?.PlanTypeID)
                                  ? "Add a card to continue"
                                  : "Add a card first, then upgrade"}
                              </button>
                            </div>
                          ) : isSubscribe &&
                          currentPlan?.data &&
                          currentPlan?.data?.[0]?.plan_id == el?.PlanID ? (
                            <div className="pricing-action">
                              {configData?.retentionSaveFlow ? (
                                <button
                                  onClick={() => navigate(PageRoutes.CancelSaveFlow)}
                                  disabled={isSkeleton}
                                  style={{ cursor: "pointer" }}
                                  className={configData.buttonCommonClass}
                                >
                                  Manage Plan
                                </button>
                              ) : currentPlan?.subscription?.pause_collection ? (
                                <button
                                  onClick={handelRestartPause}
                                  disabled={isSkeleton}
                                  style={{ cursor: "pointer" }}
                                  className={configData.buttonCommonClass}
                                >
                                  Resume
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleMangePlan()}
                                  disabled={isSkeleton}
                                  style={{ cursor: "pointer" }}
                                  className={configData.buttonCommonClass}
                                >
                                  {currentPlan?.data[0].isRecurring &&
                                  !currentPlan?.data[0].cancel_date
                                    ? "Cancel"
                                    : "Renew plan"}
                                </button>
                              )}
                            </div>
                          ) : currentPlan?.data && el?.PlanTypeID != 3 ? (
                            <UpgradeModel
                              el={el}
                              isLoading={isLoading}
                              currentPlan={currentPlan}
                              handleUpgrade={handleUpgrade}
                              isSkeleton={isSkeleton}
                            />
                          ) : (
                            <div
                              onClick={() => {
                                if (
                                  isSkeleton ||
                                  !el?.Status ||
                                  (currentPlan?.data &&
                                    currentPlan?.data[0].plan_id != el?.PlanID)
                                ) {
                                  return;
                                } else {
                                  if (!auth.AccessToken) {
                                    navigate(PageRoutes.Login);
                                  } else {
                                    !gameId && el?.PlanTypeID == 3
                                      ? handleClickOpen()
                                      : handleSubmit(el);
                                  }
                                }
                              }}
                              className="pricing-action"
                            >
                              <button
                                disabled={
                                  isSkeleton ||
                                  !el?.Status ||
                                  (currentPlan?.data &&
                                    currentPlan?.data[0].plan_id != el?.PlanID)
                                }
                                style={{
                                  cursor: "pointer",
                                  visibility:
                                    currentPlan?.data &&
                                    currentPlan?.data[0].plan_id != el?.PlanID
                                      ? "hidden"
                                      : "visible",
                                }}
                                className={configData.buttonCommonClass}
                              >
                                BUY
                              </button>{" "}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
            <br />
          </div>
          <DialogBox
            open={open}
            handleClose={handleClose}
            buttonAction={() => navigate(PageRoutes.UpcomingEvent)}
            buttonText={"Events"}
            content={
              "In order to buy a single game, you must select the game first"
            }
          />
        </Box>
      </div>
    </Box>
  );
};

export default Passes;
